// ======================================================================
// FILE:
// src/modules/finance/transactions/receipt_voucher/pages/ReceiptVoucherViewPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Receipt Voucher - View
//
// PURPOSE:
// Display a Finance Receipt Voucher in read-only mode.
//
// IMPORTANT:
// - No accounting logic
// - No direct database access
// - Uses receiptVoucherService
// - Draft vouchers can be edited/submitted
// - Submitted vouchers can be approved
// - Approved vouchers can be posted
// ======================================================================

import React, {
    useEffect,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceStatusBadge
    from "../../../components/FinanceStatusBadge";

import receiptVoucherService
    from "../services/receiptVoucherService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function ReceiptVoucherViewPage() {

    const navigate =
        useNavigate();


    const {
        id
    } = useParams();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        voucher,
        setVoucher
    ] = useState(null);


    const [
        loading,
        setLoading
    ] = useState(true);


    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    const [
        actionLoading,
        setActionLoading
    ] = useState(false);


    // ==================================================================
    // LOAD
    // ==================================================================

    useEffect(
        () => {

            loadVoucher();

        },
        [
            id
        ]
    );


    async function loadVoucher() {

        try {

            setLoading(true);

            setErrorMessage("");


            const response =
                await receiptVoucherService
                    .getReceiptVoucher(
                        id
                    );


            setVoucher(
                response
            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to load Receipt Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load Receipt Voucher."
            );

        }
        finally {

            setLoading(false);

        }

    }


    // ==================================================================
    // NAVIGATION
    // ==================================================================

    function handleBack() {

        navigate(
            "/finance/receipt-vouchers"
        );

    }


    function handleEdit() {

        navigate(
            `/finance/receipt-vouchers/edit/${id}`
        );

    }


    // ==================================================================
    // SUBMIT
    // ==================================================================

    async function handleSubmit() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await receiptVoucherService
                .submitReceiptVoucher(
                    voucher.id,
                    voucher.company_id
                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to submit Receipt Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to submit Receipt Voucher."
            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // APPROVE
    // ==================================================================

    async function handleApprove() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await receiptVoucherService
                .approveReceiptVoucher(
                    voucher.id,
                    voucher.company_id
                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to approve Receipt Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to approve Receipt Voucher."
            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // POST
    // ==================================================================

    async function handlePost() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await receiptVoucherService
                .postReceiptVoucher(
                    voucher.id,
                    voucher.company_id
                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to post Receipt Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to post Receipt Voucher."
            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // FORMAT DATE
    // ==================================================================

    function formatDate(
        value
    ) {

        if (
            !value
        ) {

            return "-";

        }


        return String(
            value
        ).slice(
            0,
            10
        );

    }


    // ==================================================================
    // FORMAT MONEY
    // ==================================================================

    function formatAmount(
        value
    ) {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        );

    }


    // ==================================================================
    // LABEL
    // ==================================================================

    function formatPayerType(
        value
    ) {

        if (
            !value
        ) {

            return "-";

        }


        return String(
            value
        )
            .replaceAll(
                "_",
                " "
            )
            .replace(
                /\b\w/g,
                char =>
                    char.toUpperCase()
            );

    }


    // ==================================================================
    // LOADING
    // ==================================================================

    if (
        loading
    ) {

        return (
            <div
                className="
                    d-flex
                    justify-content-center
                    align-items-center
                    p-5
                "
            >

                <div
                    className="
                        spinner-border
                        text-primary
                    "
                    role="status"
                />

            </div>
        );

    }


    // ==================================================================
    // ERROR / NOT FOUND
    // ==================================================================

    if (
        !voucher
    ) {

        return (
            <>

                <FinancePageHeader

                    icon="fas fa-money-bill-wave"

                    title="Receipt Voucher"

                    subtitle={
                        "View Finance Receipt Voucher."
                    }

                    breadcrumbs={[
                        {
                            label:
                                "Finance",

                            to:
                                "/finance",
                        },

                        {
                            label:
                                "Receipt Voucher",

                            to:
                                "/finance/receipt-vouchers",
                        },

                        {
                            label:
                                "View",
                        },
                    ]}

                    actions={

                        <button
                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleBack
                            }
                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Back to List

                        </button>

                    }

                />


                <div
                    className="
                        alert
                        alert-danger
                        mt-3
                    "
                >

                    {
                        errorMessage ||
                        "Receipt Voucher not found."
                    }

                </div>

            </>
        );

    }


    // ==================================================================
    // STATUS
    // ==================================================================

    const status =
        String(
            voucher.status ||
            ""
        ).toUpperCase();


    const approvalStatus =
        String(
            voucher.approval_status ||
            ""
        ).toUpperCase();
            // ==================================================================
    // ACTION VISIBILITY
    // ==================================================================

    const canEdit =
        status === "DRAFT";


    const canSubmit =
        status === "DRAFT";


    const canApprove =
        status === "SUBMITTED";


    const canPost =
        status === "APPROVED";


    // ==================================================================
    // LINES
    // ==================================================================

    const lines =
        Array.isArray(
            voucher.lines
        )
            ? voucher.lines
            : [];


    // ==================================================================
    // TOTALS
    // ==================================================================

    const totalTransactionAmount =
        lines.reduce(
            (
                total,
                line
            ) =>
                total +
                (
                    Number(
                        line.transaction_amount
                    ) || 0
                ),
            0
        );


    const totalBaseAmount =
        lines.reduce(
            (
                total,
                line
            ) =>
                total +
                (
                    Number(
                        line.base_amount
                    ) || 0
                ),
            0
        );


    // ==================================================================
    // ACTION CONFIRMATION
    // ==================================================================

    async function confirmAndSubmit() {

        const confirmed =
            window.confirm(
                "Submit this Receipt Voucher for approval?"
            );


        if (
            !confirmed
        ) {

            return;

        }


        await handleSubmit();

    }


    async function confirmAndApprove() {

        const confirmed =
            window.confirm(
                "Approve this Receipt Voucher?"
            );


        if (
            !confirmed
        ) {

            return;

        }


        await handleApprove();

    }


    async function confirmAndPost() {

        const confirmed =
            window.confirm(
                "Post this Receipt Voucher to the accounts?"
            );


        if (
            !confirmed
        ) {

            return;

        }


        await handlePost();

    }


    // ==================================================================
    // PAGE
    // ==================================================================

    return (

        <>

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-money-bill-wave"

                title={
                    voucher.voucher_no ||
                    "Receipt Voucher"
                }

                subtitle={
                    "Receipt Voucher Details"
                }

                breadcrumbs={[

                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Receipt Voucher",

                        to:
                            "/finance/receipt-vouchers",
                    },

                    {
                        label:
                            voucher.voucher_no ||
                            "View",
                    },

                ]}

                actions={

                    <div
                        className="
                            d-flex
                            gap-2
                            flex-wrap
                        "
                    >

                        {/* ==================================================
                            BACK
                        ================================================== */}

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleBack
                            }

                            disabled={
                                actionLoading
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Back

                        </button>


                        {/* ==================================================
                            EDIT
                        ================================================== */}

                        {
                            canEdit && (

                                <button

                                    type="button"

                                    className="
                                        btn
                                        btn-outline-primary
                                    "

                                    onClick={
                                        handleEdit
                                    }

                                    disabled={
                                        actionLoading
                                    }

                                >

                                    <i
                                        className="
                                            fas
                                            fa-edit
                                            me-1
                                        "
                                    />

                                    Edit

                                </button>

                            )
                        }


                        {/* ==================================================
                            SUBMIT
                        ================================================== */}

                        {
                            canSubmit && (

                                <button

                                    type="button"

                                    className="
                                        btn
                                        btn-primary
                                    "

                                    onClick={
                                        confirmAndSubmit
                                    }

                                    disabled={
                                        actionLoading
                                    }

                                >

                                    {
                                        actionLoading ? (

                                            <span
                                                className="
                                                    spinner-border
                                                    spinner-border-sm
                                                    me-1
                                                "
                                            />

                                        ) : (

                                            <i
                                                className="
                                                    fas
                                                    fa-paper-plane
                                                    me-1
                                                "
                                            />

                                        )
                                    }

                                    Submit

                                </button>

                            )
                        }


                        {/* ==================================================
                            APPROVE
                        ================================================== */}

                        {
                            canApprove && (

                                <button

                                    type="button"

                                    className="
                                        btn
                                        btn-success
                                    "

                                    onClick={
                                        confirmAndApprove
                                    }

                                    disabled={
                                        actionLoading
                                    }

                                >

                                    {
                                        actionLoading ? (

                                            <span
                                                className="
                                                    spinner-border
                                                    spinner-border-sm
                                                    me-1
                                                "
                                            />

                                        ) : (

                                            <i
                                                className="
                                                    fas
                                                    fa-check
                                                    me-1
                                                "
                                            />

                                        )
                                    }

                                    Approve

                                </button>

                            )
                        }


                        {/* ==================================================
                            POST
                        ================================================== */}

                        {
                            canPost && (

                                <button

                                    type="button"

                                    className="
                                        btn
                                        btn-dark
                                    "

                                    onClick={
                                        confirmAndPost
                                    }

                                    disabled={
                                        actionLoading
                                    }

                                >

                                    {
                                        actionLoading ? (

                                            <span
                                                className="
                                                    spinner-border
                                                    spinner-border-sm
                                                    me-1
                                                "
                                            />

                                        ) : (

                                            <i
                                                className="
                                                    fas
                                                    fa-check-double
                                                    me-1
                                                "
                                            />

                                        )
                                    }

                                    Post

                                </button>

                            )
                        }

                    </div>

                }

            />


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        <i
                            className="
                                fas
                                fa-exclamation-triangle
                                me-2
                            "
                        />

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                STATUS SUMMARY
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <div
                        className="
                            row
                            g-3
                            align-items-center
                        "
                    >

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Voucher No
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.voucher_no ||
                                    "-"
                                }

                            </div>

                        </div>


                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Voucher Date
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    formatDate(
                                        voucher.voucher_date
                                    )
                                }

                            </div>

                        </div>


                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Status
                            </div>

                            <div>

                                <FinanceStatusBadge

                                    type="journal"

                                    value={
                                        voucher.status
                                    }

                                />

                            </div>

                        </div>


                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Approval Status
                            </div>

                            <div>

                                <FinanceStatusBadge

                                    type="approval"

                                    value={
                                        voucher.approval_status
                                    }

                                />

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                RECEIPT VOUCHER DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-header"
                >

                    <h5
                        className="mb-0"
                    >

                        <i
                            className="
                                fas
                                fa-file-invoice-dollar
                                me-2
                            "
                        />

                        Receipt Voucher Details

                    </h5>

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-3"
                    >

                        {/* ==================================================
                            COMPANY
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Company
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.company_name ||
                                    voucher.company_id ||
                                    "-"
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            RECEIPT ACCOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Receipt Account
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.receipt_account_name ||
                                    voucher.receipt_account_number ||
                                    voucher.receipt_account_id ||
                                    "-"
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            PAYER TYPE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Payer Type
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    formatPayerType(
                                        voucher.payer_type
                                    )
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            PAYER
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Payer
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.payer_name ||
                                    voucher.payer_id ||
                                    "-"
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            RECEIPT MODE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Receipt Mode
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    formatPayerType(
                                        voucher.receipt_mode
                                    )
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            REFERENCE NO
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Reference No
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.reference_no ||
                                    "-"
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            REFERENCE DATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Reference Date
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    formatDate(
                                        voucher.reference_date
                                    )
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            CURRENCY
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Currency
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.currency_code ||
                                    voucher.currency_name ||
                                    voucher.currency_id ||
                                    "-"
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            EXCHANGE RATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Exchange Rate
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.exchange_rate ??
                                    "-"
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            TRANSACTION AMOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Transaction Amount
                            </div>

                            <div
                                className="
                                    fw-semibold
                                    fs-5
                                "
                            >

                                {
                                    formatAmount(
                                        voucher.transaction_amount
                                    )
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            BASE AMOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Base Amount
                            </div>

                            <div
                                className="
                                    fw-semibold
                                    fs-5
                                "
                            >

                                {
                                    formatAmount(
                                        voucher.base_amount
                                    )
                                }

                            </div>

                        </div>


                        {/* ==================================================
                            REMARKS
                        ================================================== */}

                        <div
                            className="col-12"
                        >

                            <div
                                className="text-muted small"
                            >
                                Remarks
                            </div>

                            <div
                                className="mt-1"
                            >

                                {
                                    voucher.remarks ||
                                    "-"
                                }

                            </div>

                        </div>

                    </div>

                </div>

            </div>
                        {/* ==========================================================
                ALLOCATION DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-header"
                >

                    <h5
                        className="mb-0"
                    >

                        <i
                            className="
                                fas
                                fa-list
                                me-2
                            "
                        />

                        Receipt Allocation Details

                    </h5>

                </div>


                <div
                    className="card-body p-0"
                >

                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-bordered
                                table-hover
                                align-middle
                                mb-0
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: 60,
                                        }}
                                    >
                                        #
                                    </th>

                                    <th>
                                        GL Account
                                    </th>

                                    <th>
                                        Allocation Type
                                    </th>

                                    <th>
                                        Payer / Partner
                                    </th>

                                    <th
                                        className="text-end"
                                    >
                                        Transaction Amount
                                    </th>

                                    <th
                                        className="text-end"
                                    >
                                        Base Amount
                                    </th>

                                    <th>
                                        Cost Centre
                                    </th>

                                    <th>
                                        Project
                                    </th>

                                    <th>
                                        Department
                                    </th>

                                    <th>
                                        Description
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    lines.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan={10}
                                                className="
                                                    text-center
                                                    text-muted
                                                    py-4
                                                "
                                            >

                                                No allocation lines found.

                                            </td>

                                        </tr>

                                    ) : (

                                        lines.map(
                                            (
                                                line,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        line.id ||
                                                        line.sequence_no ||
                                                        index
                                                    }
                                                >

                                                    {/* ==========================
                                                        SEQUENCE
                                                    ========================== */}

                                                    <td>

                                                        {
                                                            line.sequence_no ||
                                                            index + 1
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        ACCOUNT
                                                    ========================== */}

                                                    <td>

                                                        <div
                                                            className="fw-semibold"
                                                        >

                                                            {
                                                                line.account_name ||
                                                                line.account_code ||
                                                                line.account_id ||
                                                                "-"
                                                            }

                                                        </div>

                                                        {
                                                            line.account_code &&
                                                            line.account_name && (

                                                                <div
                                                                    className="
                                                                        small
                                                                        text-muted
                                                                    "
                                                                >

                                                                    {
                                                                        line.account_code
                                                                    }

                                                                </div>

                                                            )
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        ALLOCATION TYPE
                                                    ========================== */}

                                                    <td>

                                                        {
                                                            line.allocation_type
                                                                ? String(
                                                                    line.allocation_type
                                                                )
                                                                    .replaceAll(
                                                                        "_",
                                                                        " "
                                                                    )
                                                                : "-"
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        PARTNER
                                                    ========================== */}

                                                    <td>

                                                        {
                                                            line.business_partner_name ||
                                                            line.customer_name ||
                                                            line.vendor_name ||
                                                            line.employee_name ||
                                                            line.business_partner_id ||
                                                            line.customer_id ||
                                                            line.vendor_id ||
                                                            line.employee_id ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        TRANSACTION AMOUNT
                                                    ========================== */}

                                                    <td
                                                        className="
                                                            text-end
                                                            fw-semibold
                                                        "
                                                    >

                                                        {
                                                            formatAmount(
                                                                line.transaction_amount
                                                            )
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        BASE AMOUNT
                                                    ========================== */}

                                                    <td
                                                        className="
                                                            text-end
                                                        "
                                                    >

                                                        {
                                                            formatAmount(
                                                                line.base_amount
                                                            )
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        COST CENTRE
                                                    ========================== */}

                                                    <td>

                                                        {
                                                            line.cost_center_name ||
                                                            line.cost_center_code ||
                                                            line.cost_center_id ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        PROJECT
                                                    ========================== */}

                                                    <td>

                                                        {
                                                            line.project_name ||
                                                            line.project_code ||
                                                            line.project_id ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        DEPARTMENT
                                                    ========================== */}

                                                    <td>

                                                        {
                                                            line.department_name ||
                                                            line.department_id ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==========================
                                                        DESCRIPTION
                                                    ========================== */}

                                                    <td>

                                                        {
                                                            line.description ||
                                                            "-"
                                                        }

                                                    </td>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>


                            <tfoot>

                                <tr
                                    className="table-light"
                                >

                                    <td
                                        colSpan={4}
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        Total

                                    </td>


                                    <td
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        {
                                            formatAmount(
                                                totalTransactionAmount
                                            )
                                        }

                                    </td>


                                    <td
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        {
                                            formatAmount(
                                                totalBaseAmount
                                            )
                                        }

                                    </td>


                                    <td
                                        colSpan={4}
                                    />

                                </tr>

                            </tfoot>

                        </table>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                POSTING / JOURNAL INFORMATION
            ========================================================== */}

            {
                (
                    voucher.journal_header_id ||
                    voucher.posted_by ||
                    voucher.posted_at
                ) && (

                    <div
                        className="
                            card
                            shadow-sm
                            border-0
                            mt-3
                        "
                    >

                        <div
                            className="card-header"
                        >

                            <h5
                                className="mb-0"
                            >

                                <i
                                    className="
                                        fas
                                        fa-book
                                        me-2
                                    "
                                />

                                Accounting Information

                            </h5>

                        </div>


                        <div
                            className="card-body"
                        >

                            <div
                                className="row g-3"
                            >

                                {/* ==========================================
                                    JOURNAL
                                ========================================== */}

                                <div
                                    className="
                                        col-lg-4
                                        col-md-6
                                    "
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Journal Header ID
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >

                                        {
                                            voucher.journal_header_id ||
                                            "-"
                                        }

                                    </div>

                                </div>


                                {/* ==========================================
                                    POSTED BY
                                ========================================== */}

                                <div
                                    className="
                                        col-lg-4
                                        col-md-6
                                    "
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Posted By
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >

                                        {
                                            voucher.posted_by_name ||
                                            voucher.posted_by ||
                                            "-"
                                        }

                                    </div>

                                </div>


                                {/* ==========================================
                                    POSTED AT
                                ========================================== */}

                                <div
                                    className="
                                        col-lg-4
                                        col-md-6
                                    "
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Posted At
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >

                                        {
                                            voucher.posted_at ||
                                            "-"
                                        }

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ==========================================================
                APPROVAL INFORMATION
            ========================================================== */}

            {
                (
                    voucher.approved_by ||
                    voucher.approved_at
                ) && (

                    <div
                        className="
                            card
                            shadow-sm
                            border-0
                            mt-3
                        "
                    >

                        <div
                            className="card-header"
                        >

                            <h5
                                className="mb-0"
                            >

                                <i
                                    className="
                                        fas
                                        fa-user-check
                                        me-2
                                    "
                                />

                                Approval Information

                            </h5>

                        </div>


                        <div
                            className="card-body"
                        >

                            <div
                                className="row g-3"
                            >

                                <div
                                    className="
                                        col-lg-6
                                        col-md-6
                                    "
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Approved By
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >

                                        {
                                            voucher.approved_by_name ||
                                            voucher.approved_by ||
                                            "-"
                                        }

                                    </div>

                                </div>


                                <div
                                    className="
                                        col-lg-6
                                        col-md-6
                                    "
                                >

                                    <div
                                        className="text-muted small"
                                    >
                                        Approved At
                                    </div>

                                    <div
                                        className="fw-semibold"
                                    >

                                        {
                                            voucher.approved_at ||
                                            "-"
                                        }

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ==========================================================
                AUDIT INFORMATION
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                    mb-4
                "
            >

                <div
                    className="card-header"
                >

                    <h5
                        className="mb-0"
                    >

                        <i
                            className="
                                fas
                                fa-history
                                me-2
                            "
                        />

                        Audit Information

                    </h5>

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-3"
                    >

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Created By
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.created_by_name ||
                                    voucher.created_by ||
                                    "-"
                                }

                            </div>

                        </div>


                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Created At
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.created_at ||
                                    "-"
                                }

                            </div>

                        </div>


                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Updated By
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.updated_by_name ||
                                    voucher.updated_by ||
                                    "-"
                                }

                            </div>

                        </div>


                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <div
                                className="text-muted small"
                            >
                                Updated At
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    voucher.updated_at ||
                                    "-"
                                }

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                BOTTOM ACTIONS
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-end
                    gap-2
                    mb-4
                "
            >

                <button

                    type="button"

                    className="
                        btn
                        btn-outline-secondary
                    "

                    onClick={
                        handleBack
                    }

                    disabled={
                        actionLoading
                    }

                >

                    <i
                        className="
                            fas
                            fa-arrow-left
                            me-1
                        "
                    />

                    Back to List

                </button>


                {
                    canEdit && (

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-primary
                            "

                            onClick={
                                handleEdit
                            }

                            disabled={
                                actionLoading
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-edit
                                    me-1
                                "
                            />

                            Edit

                        </button>

                    )
                }


                {
                    canSubmit && (

                        <button

                            type="button"

                            className="
                                btn
                                btn-primary
                            "

                            onClick={
                                confirmAndSubmit
                            }

                            disabled={
                                actionLoading
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-paper-plane
                                    me-1
                                "
                            />

                            Submit

                        </button>

                    )
                }


                {
                    canApprove && (

                        <button

                            type="button"

                            className="
                                btn
                                btn-success
                            "

                            onClick={
                                confirmAndApprove
                            }

                            disabled={
                                actionLoading
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-check
                                    me-1
                                "
                            />

                            Approve

                        </button>

                    )
                }


                {
                    canPost && (

                        <button

                            type="button"

                            className="
                                btn
                                btn-dark
                            "

                            onClick={
                                confirmAndPost
                            }

                            disabled={
                                actionLoading
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-check-double
                                    me-1
                                "
                            />

                            Post

                        </button>

                    )
                }

            </div>

        </>

    );

}